import * as ReactDOM from 'react-dom'
import * as React from 'react'

import { LitElement, type PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import type { Node } from 'slate'
import type { UserProfile } from '../domain/users/types.no-deps'
import RichTextEntry from '../domain/richText/RichTextEntry'
import type { RichTextParagraph } from '../domain/richText/types.no-deps'
import type { SupportedLanguage } from '../i18n/i18n.no-deps'
import type { UnitAttribute } from '../domain/units/types.no-deps'
import type { Nullable } from '../lib/typeHelpers.no-deps'

type Events = 'onChange' | 'onSubmit'

@customElement('zaptic-rich-text')
export class RichText extends LitElement {
    public static tagName = 'zaptic-rich-text'

    public events: Events[] = ['onChange', 'onSubmit']

    private lastRenderedValue: Nullable<Node[]>

    @property({ converter: (value) => JSON.parse(value) })
    content: RichTextParagraph[]

    @property({ converter: (value) => JSON.parse(value) })
    contentkey: string

    @property({ converter: (value) => JSON.parse(value) })
    users: UserProfile[]

    @property({ converter: (value) => JSON.parse(value) })
    unitattributes: UnitAttribute[]

    @property({ converter: (value) => JSON.parse(value) })
    placeholder: string

    @property({ converter: (value) => JSON.parse(value) })
    language: SupportedLanguage

    @property({ converter: (value) => JSON.parse(value) })
    disabled?: boolean

    @property({ converter: (value) => JSON.parse(value) })
    readonly?: false

    @property({ converter: (value) => JSON.parse(value) })
    plaintext?: boolean

    @property({ converter: (value) => JSON.parse(value) })
    iseditmode?: boolean

    updated(changedProperties: PropertyValues<RichText>) {
        if (changedProperties.has('content')) {
            // Force a ren-render of the React component when the `content` attribute is
            // out of sync with the last rendered value. This is useful for when the `content` value is changed
            // programmatically in Elm, e.g. Undo/Redo feature.
            if (
                this.lastRenderedValue !== null &&
                JSON.stringify(this.lastRenderedValue) !== JSON.stringify(this.content)
            ) {
                this.contentkey = `${this.contentkey}-${Date.now()}`
                this.lastRenderedValue = this.content
            }
        }
    }

    private handleChange = (value: Node[]) => {
        const event = new CustomEvent('onChange', { detail: value, bubbles: true })
        this.lastRenderedValue = value
        this.dispatchEvent(event)
    }

    private handleSubmit = () => {
        const event = new CustomEvent('onSubmit', { bubbles: true })
        this.dispatchEvent(event)
    }

    render() {
        if (this.readonly && this.content == null) {
            return
        }

        ReactDOM.render(
            React.createElement(RichTextEntry, {
                onChange: this.handleChange,
                onSubmit: this.handleSubmit,
                content: this.content,
                contentKey: this.contentkey,
                users: this.users,
                unitAttributes: this.unitattributes,
                placeholder: this.placeholder,
                disabled: this.disabled,
                readOnly: this.readonly,
                language: this.language,
                usePlain: this.plaintext,
                isEditMode: this.iseditmode,
            }),
            this
        )
    }

    protected createRenderRoot() {
        return this
    }
}
